import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from '../../../../../components/ui/Dialog';
import { Button } from '../../../../../components/ui/Button';
import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import { InputField } from '../../../../../components/formFields/InputField';
import Bar from '../../../../../components/elements/Bar';
import { Plus } from 'lucide-react';
import Label from '@/components/Label';
import FormList from '../../../../../components/elements/FormList';
import FileUploader from '../../../../../components/FileUploader';

type BiblioModalProps = {
  visible: boolean;
  title: string;
  formProps?: any;
  onSubmit: (id: any, values: any) => void;
  onClose: () => void;
  classification?: any[];
};

const BiblioModal = ({ visible, title, formProps, onSubmit, onClose }: BiblioModalProps) => {
  const [visibleAddFileDialog, setVisibleAddFileDialog] = useState(false);

  const ValidationSchema = Yup.object().shape({
    title: Yup.string().nullable().required('Povinné pole'),
    designation: Yup.string().nullable().required('Povinné pole'),
  });

  const formRef = useRef<any>(null);

  const handleAddFile = (file: any) => {
    formRef.current.setFieldValue('files', [...formRef.current.values.files, file]);

    setVisibleAddFileDialog(false);
  };

  const handleDeleteFile = (item: any) => {
    if (!formRef?.current) return null;

    const indexToRemove = formRef.current.values.files.find((ele: any) => ele.id === item.id);
    const newValues = formRef.current.values.files.filter(
      (_: any, i: any) => i.id !== indexToRemove
    );

    formRef.current.setFieldValue('files', newValues);
  };

  const onSubmitButton = () => {
    if (!formRef?.current) return null;
    formRef.current.submitForm();
  };

  return (
    <Dialog open={visible} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <Formik
            innerRef={formRef}
            enableReinitialize={true}
            validationSchema={ValidationSchema}
            initialValues={{
              title: formProps?.title || null,
              designation: formProps?.designation || null,
              publication_year: formProps?.publication_year || null,
              files: formProps?.files || [],
            }}
            onSubmit={(values) => {
              onSubmit(formProps?.id, {
                ...values,
                files: values.files.map((file: any) => file.id),
              });
            }}
          >
            {(props) => {
              const { values } = props;
              return (
                <>
                  <Form>
                    <div className="space-y-1">
                      <div>
                        <Label name="designation" title="Označení" required />
                        <Field
                          name="designation"
                          value={values.designation || null}
                          placeholder="Označení"
                          component={InputField}
                          data-testid="biblio-modal-designation-input"
                        />
                      </div>

                      <div>
                        <Label name="title" title="Název" required />
                        <Field
                          name="title"
                          value={values.title || null}
                          placeholder="Název"
                          component={InputField}
                          data-testid="biblio-modal-title-input"
                        />
                      </div>

                      <div>
                        <Label name="publication_year" title="Rok vydání" />
                        <Field
                          name="publication_year"
                          value={values.publication_year || null}
                          placeholder="Rok vydání"
                          type="number"
                          component={InputField}
                          data-testid="biblio-modal-publication-year-input"
                        />
                      </div>

                      <div className="mb-1">
                        <Bar
                          text="Soubory"
                          content={[
                            <Plus
                              key={1}
                              className="h-4 w-4"
                              onClick={() => setVisibleAddFileDialog(true)}
                              data-testid="biblio-modal-add-file-btn"
                            />,
                          ]}
                        />
                        <FormList data={values.files} onDelete={handleDeleteFile} />
                      </div>
                      <FileUploader
                        visible={visibleAddFileDialog}
                        onSubmit={handleAddFile}
                        url={'/api/v1/biblio/upload-file/'}
                        onClose={() => setVisibleAddFileDialog(false)}
                      />
                    </div>
                  </Form>
                </>
              );
            }}
          </Formik>
        </DialogBody>
        <DialogFooter>
          <Button
            key={1}
            variant="outline"
            onClick={() => onClose()}
            data-testid="biblio-modal-close-btn"
          >
            Zavřít
          </Button>
          <Button key={2} onClick={() => onSubmitButton()} data-testid="biblio-modal-submit-btn">
            Potvrdit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BiblioModal;
